/**
 * Where "Get the app" actually goes.
 *
 * The App Store listing does not exist yet. Checked 2026-09-06:
 *
 *   curl -sIL https://apps.apple.com/app/manticscore/id6760257488  -> 404
 *   curl -s "https://itunes.apple.com/lookup?id=6760257488"        -> resultCount 0
 *   curl -s ".../lookup?bundleId=com.bairisland.manticscore"       -> resultCount 0
 *
 * `resultCount: 0` is Apple saying no such app, not a transient failure, and
 * the bundle ID is the one this repo's AASA claims. So the ID we shipped was
 * either a placeholder or minted before the listing existed.
 *
 * That URL was hardcoded in five places, including `/invite/:code` — which its
 * own header comment calls "a cold-traffic entry point". The worst-affected
 * path was the one we send invited users down: arrive from a friend, tap the
 * primary CTA, hit an App Store 404 (#278).
 *
 * The rest of the site had already solved this: seven files ship a TestFlight
 * link, and it returns 200. So the fix is not a new destination, it is to make
 * these five agree with the other seven. Flip `APP_STORE_IS_LIVE` when the
 * listing exists and every call site follows, because they all read
 * `appDownloadUrl()`.
 */

/**
 * The intended listing. Exported for the flip and for tests, deliberately not
 * linked while the listing 404s.
 */
export const APP_STORE_URL = "https://apps.apple.com/app/manticscore/id6760257488";

/**
 * Set to true once `itunes.apple.com/lookup?id=...` returns a result. One line,
 * one deploy, and every "Get the app" affordance moves to the App Store.
 */
export const APP_STORE_IS_LIVE = false;

/**
 * The public beta, and the app distribution that actually works today. Kept
 * byte-identical to the seven other copies in `src/` — see appStore.test.ts,
 * which fails if they drift.
 */
export const TESTFLIGHT_URL = "https://testflight.apple.com/join/4Ym7X41D";

/**
 * The href for any "Get the app" affordance. Never returns a URL we know to be
 * dead.
 */
export function appDownloadUrl(): string {
  return APP_STORE_IS_LIVE ? APP_STORE_URL : TESTFLIGHT_URL;
}

/**
 * The label that goes with it. TestFlight is still "the app", so the wording
 * holds either way; it is the destination that changes.
 */
export function appDownloadLabel(full = false): string {
  return full ? "Get ManticScore" : "Get the app";
}
